import React, { useState } from "react";
import SEOHead from "../../components/SEO/SEOHead";
import LegalPageLayout from "../../components/Legal/LegalPageLayout";

const Disclaimer = () => {
  const [activeSection, setActiveSection] = useState("general");

  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const sections = [
    { id: "general", title: "General Disclaimer" },
    { id: "service-availability", title: "Service Availability" },
    { id: "tournament-information", title: "Tournament Information" },
    { id: "third-party", title: "Third-Party Content" },
    { id: "financial-legal", title: "Financial & Legal" },
    { id: "liability", title: "Limitation of Liability" },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <SEOHead
        title="Legal Disclaimer - Bowling Tournament App"
        description="Read our legal disclaimer covering service availability, tournament information, third-party content, and limitation of liability."
        keywords={[
          "legal disclaimer",
          "terms",
          "liability",
          "service availability",
          "tournament disclaimer",
        ]}
        canonicalUrl="/disclaimer"
        noIndex={false}
        additionalMeta={[{ name: "last-modified", content: currentDate }]}
      />
      <LegalPageLayout
        title="Legal Disclaimer"
        lastUpdated={currentDate}
        sections={sections}
        activeSection={activeSection}
        onSectionClick={scrollToSection}
      >
        <div className="space-y-12">
                {/* General Disclaimer Section */}
                <section id="general">
                  <h2 className="heading-display text-xl mb-4">
                    General Disclaimer
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p>
                      The Bowling Tournament App is provided "as is" and "as
                      available" without any warranties, representations, or
                      guarantees of any kind, either express or implied. We make
                      no claims regarding the accuracy, completeness, reliability,
                      or suitability of the information, services, or content
                      provided through our platform.
                    </p>
                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Important:</strong> Users should independently
                        verify all information and not rely solely on our
                        platform for critical decisions related to tournament
                        participation or outcomes.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Service Availability Section */}
                <section id="service-availability">
                  <h2 className="heading-display text-xl mb-4">
                    Service Availability & Limitations
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Platform Availability
                    </h3>
                    <p className="mb-4">
                      While we strive to maintain high availability, we do not
                      guarantee uninterrupted access to our platform. The service
                      may be temporarily unavailable due to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>Scheduled maintenance and updates</li>
                      <li>Technical issues or system failures</li>
                      <li>Network connectivity problems</li>
                      <li>Third-party service disruptions</li>
                      <li>Force majeure events</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Feature Limitations
                    </h3>
                    <p className="mb-4">
                      Our platform may have limitations including:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>Browser compatibility requirements</li>
                      <li>Device-specific functionality</li>
                      <li>Geographic restrictions</li>
                      <li>Data storage limitations</li>
                      <li>Performance variations based on usage</li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Note:</strong> We reserve the right to modify,
                        suspend, or discontinue any part of our service at any
                        time without prior notice.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Tournament Information Section */}
                <section id="tournament-information">
                  <h2 className="heading-display text-xl mb-4">
                    Tournament Information & Standings
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Tournament Data Accuracy
                    </h3>
                    <p className="mb-4">
                      While we strive for accuracy, tournament information
                      displayed on our platform may contain errors or be subject
                      to change. We are not responsible for:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>
                        Inaccuracies in tournament schedules, rules, or results
                      </li>
                      <li>
                        Changes made by tournament organizers after publication
                      </li>
                      <li>
                        Discrepancies between displayed and actual scores
                      </li>
                      <li>Delays in updating tournament information</li>
                      <li>
                        Technical issues affecting score submission or display
                      </li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Tournament Organizer Responsibility
                    </h3>
                    <p className="mb-4">
                      Tournament organizers are responsible for:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>Providing accurate tournament information</li>
                      <li>Maintaining up-to-date schedules and results</li>
                      <li>
                        Ensuring compliance with applicable laws and regulations
                      </li>
                      <li>Handling participant disputes and concerns</li>
                      <li>Managing tournament logistics and operations</li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Important:</strong> We act as a platform
                        provider and are not responsible for tournament outcomes,
                        disputes, or decisions made by tournament organizers.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Third-Party Content Section */}
                <section id="third-party">
                  <h2 className="heading-display text-xl mb-4">
                    Third-Party Content & Links
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      External Links
                    </h3>
                    <p className="mb-4">
                      Our platform may contain links to third-party websites,
                      services, or content. We do not:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>
                        Endorse or guarantee the accuracy of third-party content
                      </li>
                      <li>Control the privacy practices of external sites</li>
                      <li>
                        Accept responsibility for third-party services or
                        products
                      </li>
                      <li>
                        Monitor or verify external content on an ongoing basis
                      </li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      User-Generated Content
                    </h3>
                    <p className="mb-4">
                      Users may submit content to our platform. We are not
                      responsible for:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>
                        The accuracy or reliability of user-submitted information
                      </li>
                      <li>Content that violates our terms of service</li>
                      <li>
                        Disputes between users regarding submitted content
                      </li>
                      <li>Loss or corruption of user-generated data</li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Recommendation:</strong> Always verify
                        information from external sources and exercise caution
                        when accessing third-party websites or services.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Financial & Legal Section */}
                <section id="financial-legal">
                  <h2 className="heading-display text-xl mb-4">
                    Financial & Legal Disclaimers
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Payment Processing
                    </h3>
                    <p className="mb-4">
                      When an organization turns on Card payments, Stripe
                      processes the card for that organization. The organization
                      is the merchant of record. Bowling Tournament App is the
                      software platform and does not hold entry money.
                    </p>
                    <p className="mb-4">
                      Payment collected outside Pay now, if any, is between you
                      and the organization.
                    </p>
                    <p>
                      We are not the merchant for entry fees. Refunds for Pay
                      now go through the organization and Stripe. See the{" "}
                      <a
                        href="/refund-policy"
                        className="text-brand-gold-ink hover:text-brand-charcoal underline"
                      >
                        Refund policy
                      </a>
                      .
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Legal Compliance
                    </h3>
                    <p className="mb-4">
                      Users are responsible for ensuring compliance with:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>Local, state, and federal laws</li>
                      <li>Tournament rules and regulations</li>
                      <li>Tax obligations</li>
                      <li>Licensing requirements</li>
                      <li>Insurance requirements</li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Legal Advice:</strong> This disclaimer does not
                        constitute legal advice. Users should consult with
                        qualified legal professionals for specific legal
                        questions or concerns.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Limitation of Liability Section */}
                <section id="liability">
                  <h2 className="heading-display text-xl mb-4">
                    Limitation of Liability
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p>
                      To the maximum extent permitted by applicable law, Bowling
                      Tournament App, its officers, directors, employees, agents,
                      and affiliates shall not be liable for any direct,
                      indirect, incidental, special, consequential, or punitive
                      damages arising from or relating to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>Use of our platform or services</li>
                      <li>Loss of data or information</li>
                      <li>Interruption of service</li>
                      <li>Tournament outcomes or disputes</li>
                      <li>Financial losses or damages</li>
                      <li>Third-party actions or content</li>
                      <li>Technical failures or errors</li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Important:</strong> These limitations do not
                        apply to damages caused by our gross negligence or
                        intentional misconduct, or where prohibited by applicable
                        law.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Contact & Updates Section */}
                <section className="border-t border-brand-charcoal/10 pt-8">
                  <h2 className="heading-display text-xl mb-4">
                    Contact & Updates
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Questions or Concerns?
                    </h3>
                    <p className="mb-4">
                      If you have questions about this disclaimer or need
                      clarification on any points, please contact us:
                    </p>
                    <p className="font-medium">
                      Email: privacy@bowlingtournamentapp.com
                    </p>
                    <p className="text-sm text-brand-muted">
                      We typically respond within 24-48 hours
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Disclaimer Updates
                    </h3>
                    <p>
                      We may update this disclaimer to reflect changes in our
                      services or legal requirements.
                    </p>
                    <p className="font-medium mt-4">
                      Last Updated: {currentDate}
                    </p>
                    <p className="text-sm text-brand-muted">
                      Significant changes will be communicated via email to
                      registered users
                    </p>
                  </div>
                </section>
        </div>
      </LegalPageLayout>
    </>
  );
};

export default Disclaimer;
